/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import api from "./axiosConfig";
import { registerDevice, logoutDevice } from "./services/deviceService";
import backgroundGPSService from "./services/backgroundGPSService";
import { esAdministrador } from "./utils/perfil";

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [tieneFirma, setTieneFirma] = useState(null); // null = no verificado, true/false = verificado

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    const savedAccessToken = localStorage.getItem("accessToken");
    const savedRefreshToken = localStorage.getItem("refreshToken");
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    if (savedAccessToken) {
      setAccessToken(savedAccessToken);
      // Guardar token en IndexedDB para el Service Worker
      backgroundGPSService.guardarTokenAuth(savedAccessToken).catch((err) => {
        console.warn("No se pudo sincronizar token con IndexedDB:", err);
      });
    }
    if (savedRefreshToken) {
      setRefreshToken(savedRefreshToken);
    }

    const handleTokensUpdated = async () => {
      const updatedAccessToken = localStorage.getItem("accessToken");
      const updatedRefreshToken = localStorage.getItem("refreshToken");
      setAccessToken(updatedAccessToken);
      setRefreshToken(updatedRefreshToken);
      // Actualizar token en IndexedDB
      if (updatedAccessToken) {
        try {
          await backgroundGPSService.guardarTokenAuth(updatedAccessToken);
        } catch (err) {
          console.warn("No se pudo actualizar token en IndexedDB:", err);
        }
      }
    };

    window.addEventListener("tokens_updated", handleTokensUpdated);

    return () => {
      window.removeEventListener("tokens_updated", handleTokensUpdated);
    };
  }, []);

  /**
   * Función para cargar el vehículo principal del conductor
   */
  const fetchVehiculoPrincipal = useCallback(async (conductorId) => {
    if (!conductorId) return null;

    try {
      const response = await api.get("/conductor/vehiculo-principal");
      console.log("Respuesta del vehículo principal:", response.data);

      // El backend devuelve: { success: true, data: { id, vehiculo_id, vehiculo: {...} } }
      // o en caso de error: { success: false, message: "..." }

      if (!response.data?.success) {
        console.warn("No se encontró vehículo principal:", response.data?.message);
        return null;
      }

      const vehiculoData = response.data.data;

      // Manejar respuesta vacía o null
      if (!vehiculoData || !vehiculoData.vehiculo) {
        console.warn("Respuesta de vehículo vacía o sin datos de vehículo");
        return null;
      }

      // Formatear el vehículo para mantener compatibilidad con el Sidebar
      // El Sidebar busca: user.vehiculoPrincipal.Vehiculo.placa
      const vehiculoFormateado = {
        id: vehiculoData.id,
        vehiculo_id: vehiculoData.vehiculo_id,
        conductor_id: vehiculoData.conductor_id,
        es_principal: vehiculoData.es_principal,
        vehiculo: vehiculoData.vehiculo,
        // Agregar con mayúscula para compatibilidad con Sidebar
        Vehiculo: vehiculoData.vehiculo
      };

      console.log("Vehículo formateado:", vehiculoFormateado);
      return vehiculoFormateado;
    } catch (error) {
      // Error 404 significa que no tiene vehículo asignado
      if (error.response?.status === 404) {
        console.warn("Conductor no tiene vehículo principal asignado");
        return null;
      }
      console.error("Error al cargar el vehículo principal:", error);
      return null;
    }
  }, []);

  // Efecto para cargar vehículo principal al iniciar o cuando cambia el usuario
  useEffect(() => {
    async function loadVehiculoPrincipal() {
      // Solo cargar si hay usuario y no tiene vehículo (el administrador de vehículos no tiene vehículo propio)
      if (user && user.id && !user.vehiculoPrincipal && !esAdministrador(user)) {
        const vehiculo = await fetchVehiculoPrincipal(user.id);
        if (vehiculo) {
          const updatedUser = { ...user, vehiculoPrincipal: vehiculo };
          setUser(updatedUser);
          localStorage.setItem("user", JSON.stringify(updatedUser));
          localStorage.setItem("vehiculoPrincipal", JSON.stringify(vehiculo));
        }
      }
    }
    loadVehiculoPrincipal();
  }, [user, fetchVehiculoPrincipal]);

  /**
   * Función para iniciar sesión.
   * Carga el vehículo principal y registra el dispositivo después del login.
   */
  const loginUser = async (userData, newAccessToken, newRefreshToken) => {
    // Guardar tokens primero para que las peticiones subsecuentes funcionen
    localStorage.setItem("accessToken", newAccessToken);
    localStorage.setItem("refreshToken", newRefreshToken);
    localStorage.setItem("token", newAccessToken); // Para compatibilidad con Service Worker
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);

    // Guardar token en IndexedDB para que el Service Worker pueda accederlo
    try {
      await backgroundGPSService.guardarTokenAuth(newAccessToken);
    } catch (error) {
      console.warn("No se pudo guardar token en IndexedDB:", error);
    }

    // El vehículo de una sesión anterior (otro usuario u otro perfil) no debe quedar guardado
    localStorage.removeItem("vehiculoPrincipal");

    // Administrador de vehículos: solo lectura de los traslados de sus vehículos. Sin vehículo propio,
    // sin registro de dispositivo (no recibe notificaciones push) ni ubicación.
    if (esAdministrador(userData)) {
      let vehiculos = [];
      try {
        const response = await api.get("/admin-vehiculo/perfil");
        vehiculos = response.data?.data?.vehiculos || [];
      } catch (error) {
        console.warn("No se pudieron cargar los vehículos del administrador:", error);
      }
      const administrador = { ...userData, vehiculos };
      localStorage.setItem("user", JSON.stringify(administrador));
      setUser(administrador);
      return;
    }

    // Cargar vehículo principal inmediatamente
    const vehiculo = await fetchVehiculoPrincipal(userData.id);
    const userWithVehiculo = vehiculo
      ? { ...userData, vehiculoPrincipal: vehiculo }
      : userData;

    // Guardar usuario con vehículo
    localStorage.setItem("user", JSON.stringify(userWithVehiculo));
    if (vehiculo) {
      localStorage.setItem("vehiculoPrincipal", JSON.stringify(vehiculo));
    }
    setUser(userWithVehiculo);

    // Registrar dispositivo en segundo plano (no bloquear el login)
    registerDeviceWithLocation();
  };

  /**
   * Registrar dispositivo con ubicación si está disponible
   */
  const registerDeviceWithLocation = () => {
    // Intentar obtener ubicación y registrar
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          registerDevice({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        () => {
          // Si no hay ubicación, registrar sin ella
          registerDevice();
        },
        { timeout: 10000, maximumAge: 60000 }
      );
    } else {
      registerDevice();
    }
  };

  /**
   * Función para cerrar sesión.
   */
  const logoutUser = async () => {
    // Cerrar sesión en el servidor (el administrador no registra dispositivo)
    try {
      if (esAdministrador(user)) {
        await api.post("/admin-vehiculo/logout");
      } else {
        await logoutDevice();
      }
    } catch (error) {
      console.error("Error al cerrar sesión del dispositivo:", error);
    }

    // Eliminar token de IndexedDB
    try {
      await backgroundGPSService.eliminarTokenAuth();
    } catch (error) {
      console.warn("No se pudo eliminar token de IndexedDB:", error);
    }

    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("token");
    localStorage.removeItem("vehiculoPrincipal");
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
  };

  /**
   * Cambia al otro perfil de la misma persona (conductor ↔ administrador de vehículos) sin pedir la
   * contraseña. La sesión anterior se cierra en el servidor (se revoca su refresh token).
   */
  const cambiarPerfil = async (perfil) => {
    const { data } = await api.post("/auth/app/cambiar-perfil", {
      perfil,
      refreshToken: localStorage.getItem("refreshToken"),
    });
    setTieneFirma(null);
    await loginUser(
      {
        ...data.user,
        requiere_cambio_password: data.requiere_cambio_password,
        perfiles: data.perfiles,
      },
      data.accessToken,
      data.refreshToken
    );
    return data;
  };

  /**
   * Función para actualizar los tokens.
   */
  const updateTokens = async (newAccessToken, newRefreshToken) => {
    localStorage.setItem("accessToken", newAccessToken);
    localStorage.setItem("refreshToken", newRefreshToken);
    localStorage.setItem("token", newAccessToken);
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);

    // Actualizar token en IndexedDB para el Service Worker
    try {
      await backgroundGPSService.guardarTokenAuth(newAccessToken);
    } catch (error) {
      console.warn("No se pudo actualizar token en IndexedDB:", error);
    }
  };

  /**
   * Función para verificar si el conductor tiene firma registrada
   */
  const verificarFirma = useCallback(async () => {
    try {
      const response = await api.get('/conductor/firma');
      console.log('=== VERIFICAR FIRMA - RESPONSE ===');
      console.log('Response completo:', response.data);

      // El backend retorna: { success: true, data: { tiene_firma, firma_imagen, ... } }
      const tieneFirmaRegistrada = response.data?.data?.firma_imagen ? true : false;

      console.log('Tiene firma registrada:', tieneFirmaRegistrada);
      console.log('Firma imagen existe:', !!response.data?.data?.firma_imagen);

      setTieneFirma(tieneFirmaRegistrada);
      return tieneFirmaRegistrada;
    } catch (error) {
      console.error('Error al verificar firma:', error);
      setTieneFirma(false);
      return false;
    }
  }, []); // Sin dependencias porque solo usa setTieneFirma que es estable

  /**
   * Función para refrescar el vehículo principal manualmente
   */
  const refreshVehiculoPrincipal = useCallback(async () => {
    if (!user || !user.id) return null;

    const vehiculo = await fetchVehiculoPrincipal(user.id);
    if (vehiculo) {
      const updatedUser = { ...user, vehiculoPrincipal: vehiculo };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      localStorage.setItem("vehiculoPrincipal", JSON.stringify(vehiculo));
    }
    return vehiculo;
  }, [user, fetchVehiculoPrincipal]);

  const contextValue = useMemo(
    () => ({
      user,
      accessToken,
      refreshToken,
      tieneFirma,
      setUser,
      loginUser,
      logoutUser,
      cambiarPerfil,
      updateTokens,
      verificarFirma,
      refreshVehiculoPrincipal,
    }),
    [user, accessToken, refreshToken, tieneFirma, verificarFirma, refreshVehiculoPrincipal]
  );

  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
